import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import {
  AssignIcon,
  BlackDownArrow,
  BlackExportIcon,
  BlackRefrechIcon,
  BlackUpArrow,
  ChangePriorityIcon,
  DeleteRedIcon,
  DownArrowSelect,
  PaginationLeftArrow,
  PaginationRightArrow,
  threeDotsIcon,
  UpdatedStatusIcon,
  ViewDetailsIcon,
} from "../../assets/icons";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";

import PopUpModal from "../../modals/PopUpModal";
import {
  reqToCreateFeedBack,
  reqToGetAllFeedback,
  reqToGetFeedBackStatus,
  reqToGetFeedBackType,
  reqToGetFeedbackById,
  reqToUpdateFeedback,
  reqToDeleteFeedback,
  clearFeedbackDetail,
  reqToAssignFeedback,
  reqToUpdateFeedbackStatus,
  reqToUpdateFeedbackPriority,
  reqToAddVote,
} from "../../redux/slices.js/feedback/feedBackSlice";
import { reqToGetStaffList } from "../../redux/slices.js/staff/staffSlice";
import { use } from "react";
import FeedbackDetailsModal from "../../components/FeedbackDetailsModal";

const FeedbackList = () => {
  const types = useSelector((state) => state?.feedbackslice?.type);
  const statuses = useSelector((state) => state?.feedbackslice?.status);

  const { feedbackType, priority } = types || [];

  const typeOptions = useMemo(() => {
    const options = (feedbackType || []).map((item) => ({
      value: item.id?.toString() || "",
      label: item.name || "Unknown Type",
    }));
    return [{ value: "all", label: "All Types" }, ...options];
  }, [feedbackType]);

  const statusOptions = useMemo(() => {
    const options = (statuses || []).map((item) => ({
      value: item.id?.toString() || "",
      label: item.name || "Unknown Status",
    }));
    return [{ value: "all", label: "All Statuses" }, ...options];
  }, [statuses]);

  const sortOptions = useMemo(() => {
    const prioritySortOptions = (priority || []).map((item) => ({
      value: item.id?.toString() || "",
      label: item.name || "Unknown Priority",
    }));

    return [
      { value: "newest", label: "Newest" },
      { value: "oldest", label: "Oldest" },
      { value: "most-voted", label: "Most Voted" },
      ...prioritySortOptions,
    ];
  }, [priority]);

  const dropdownRef = useRef(null);
  const [openRow, setOpenRow] = useState(null);

  const [typeFilter, setTypeFilter] = useState(
    typeOptions[0] || { value: "all", label: "All Types" }
  );
  const [statusFilter, setStatusFilter] = useState(
    statusOptions[0] || { value: "all", label: "All Statuses" }
  );
  const [sortOption, setSortOption] = useState(
    sortOptions[0] || { value: "newest", label: "Newest" }
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [updatingVote, setUpdatingVote] = useState(null);
  const [userVotes, setUserVotes] = useState({});

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // 'view', 'edit', 'create', 'assign', 'status', 'priority', 'delete'
  const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
  const [createProgramLoader, setCreateProgramLoader] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const staffList = useSelector((state) => state.staff.staffList);
  const feedbackState = useSelector((state) => state?.feedbackslice);

  const {
    type = { feedbackType: [], priority: [] },
    status: statusOptionsFromAPI = [],
    feedbackList = [],
    feedbackDetail = null,
    updateLoader = false,
  } = feedbackState || {};

  const paginatedData = useMemo(() => {
    return feedbackList || [];
  }, [feedbackList]);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      reqToGetStaffList({
        data: {},
        onSuccess: (res) => {},
        onFailure: (err) => {},
      })
    );
  }, [dispatch]);

  // UPDATE the fetchInitialData function (lines 169-179):
  const fetchInitialData = () => {
    fetchFeedbackList();
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (feedbackList && feedbackList.length > 0) {
      const initialVotes = {};
      feedbackList.forEach((item) => {
        if (item.is_voted !== undefined && item.is_voted !== null) {
          initialVotes[item.id] = item.is_voted;
        }
      });
      setUserVotes(initialVotes);
    }
  }, [feedbackList]);

  // Transform API data to options with correct structure

  // Add this useMemo after your existing transformations
  const staffOptions = useMemo(() => {
    return (staffList || [])
      .filter((staff) => staff.status === "active")
      .map((staff) => ({
        value: staff.id?.toString() || "",
        label: staff.name || "Unknown Staff",
        originalData: staff, // Keep original data if needed
      }));
  }, [staffList]);
  const feedbackTypeOptions = useMemo(() => {
    return (type?.feedbackType || []).map((item) => ({
      value: item.id?.toString() || "",
      label: item.name || "Unknown",
    }));
  }, [type?.feedbackType]);

  const priorityOptions = useMemo(() => {
    return (type?.priority || []).map((item) => ({
      value: item.id?.toString() || "",
      label: item.name || "Unknown",
    }));
  }, [type?.priority]);

  const statusOptionsFormatted = useMemo(() => {
    return (statusOptionsFromAPI || []).map((item) => ({
      value: item.id?.toString() || "",
      label: item.name || "Unknown",
    }));
  }, [statusOptionsFromAPI]);

  // Helper functions with safe access
  const getTypeNameById = (typeId) => {
    const typeObj = type?.feedbackType?.find((item) => item.id === typeId);
    return typeObj?.name || "N/A";
  };

  const getPriorityNameById = (priorityId) => {
    const priorityObj = type?.priority?.find((item) => item.id === priorityId);
    return priorityObj?.name || "N/A";
  };

  const getStatusNameById = (statusId) => {
    const statusObj = statusOptionsFromAPI?.find(
      (item) => item.id === statusId
    );
    return statusObj?.name || "New";
  };

  const getStatusStyle = (statusName) => {
    switch (statusName) {
      case "New":
        return "bg-[#FFF7E6] text-[#976700]";
      case "Closed":
        return "bg-[#EBEBEBA3] text-[#5C5B62]";
      case "Under Review":
      case "In Review":
        return "bg-[#E6EFFF] text-[#4285F4]";
      case "In Progress":
        return "bg-[#E6F7FF] text-[#1890FF]";
      case "Planned":
        return "bg-[#F6FFED] text-[#52C41A]";
      case "Released":
        return "bg-[#F9F0FF] text-[#722ED1]";
      default:
        return "bg-[#F2F2F2] text-[#5C5B62]";
    }
  };

  const getPriorityStyle = (priorityName) => {
    switch (priorityName) {
      case "High":
        return "bg-[#FFE3D4] text-[#B13D00]";
      case "Medium":
        return "bg-[#FFF7E6] text-[#976700]";
      case "Low":
        return "bg-[#F6FFED] text-[#277600]";
      default:
        return "bg-[#F2F2F2] text-[#5C5B62]";
    }
  };

  // Fetch data on component mount
  // UPDATE this useEffect (lines 153-161) to include filters and sorting:
  useEffect(() => {
    fetchFeedbackList();
  }, [currentPage, pageSize, typeFilter, statusFilter, sortOption]); // Add dependencies

  // ADD this useEffect after your existing useEffects:
  useEffect(() => {
    // Load initial feedback types and statuses
    dispatch(
      reqToGetFeedBackStatus({
        data: {},
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
    dispatch(
      reqToGetFeedBackType({
        data: {},
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
  }, [dispatch]);

  // UPDATE the fetchFeedbackList function to debug the API response:
  const fetchFeedbackList = () => {
    // Build query parameters for server-side filtering and pagination
    const params = {
      page: currentPage,
      limit: pageSize,
    };

    // Add filters if not "all"
    if (typeFilter.value !== "all") {
      params.type = typeFilter.value;
    }

    if (statusFilter.value !== "all") {
      params.status = statusFilter.value;
    }

    // Add sorting
    if (sortOption.value !== "newest") {
      params.sort = sortOption.value;
    }

    dispatch(
      reqToGetAllFeedback({
        data: params,
        onSuccess: (res) => {
          // Get total count from API response
          let totalCountFromAPI = 0;

          // Try different possible locations for total count
          if (res?.headers?.["x-total-count"]) {
            totalCountFromAPI = parseInt(res.headers["x-total-count"]);
          } else if (res?.data?.totalCount) {
            totalCountFromAPI = parseInt(res.data.totalCount);
          } else if (res?.data?.data?.totalCount) {
            totalCountFromAPI = parseInt(res.data.data.totalCount);
          } else if (res?.totalCount) {
            totalCountFromAPI = parseInt(res.totalCount);
          } else if (res?.data?.total) {
            totalCountFromAPI = parseInt(res.data.total);
          } else {
            // Fallback: if no total count provided, use the length of current data
            totalCountFromAPI =
              res?.data?.data?.length || res?.data?.length || 0;
          }

          setTotalCount(totalCountFromAPI);

          const calculatedTotalPages = Math.ceil(totalCountFromAPI / pageSize);
          setTotalPages(calculatedTotalPages);
        },
        onFailure: (error) => {
          console.error("Error fetching feedback:", error);
        },
      })
    );
  };

  // Pagination calculations

  // ADD this useEffect after your existing useEffects:
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenRow(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Pagination
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  // Modal handlers - UPDATED
  const handleModalConfirm = () => {
    if (modalMode === "delete") {
      // Directly handle delete without form
      handleDeleteConfirm();
    } else if (modalMode === "view") {
      // Just close the view modal
      setShowModal(false);
    } else if (formRef.current) {
      // For form-based modals, trigger form submission
      formRef.current.dispatchEvent(
        new Event("submit", { cancelable: true, bubbles: true })
      );
    }
  };

  const handleDeleteConfirm = () => {
    setCreateProgramLoader(true);
    dispatch(
      reqToDeleteFeedback({
        id: selectedFeedbackId,
        onSuccess: () => {
          setCreateProgramLoader(false);
          setShowModal(false);
          fetchFeedbackList();
        },
        onFailure: () => {
          setCreateProgramLoader(false);
        },
      })
    );
  };

  const handleViewDetails = (feedbackId) => {
    setSelectedFeedbackId(feedbackId);
    setModalMode("view");
    dispatch(
      reqToGetFeedbackById({
        id: feedbackId,
        onSuccess: () => {
          setShowModal(true);
        },
        onFailure: () => {},
      })
    );
  };

  const handleEditFeedback = (feedbackId) => {
    setSelectedFeedbackId(feedbackId);
    setModalMode("edit");
    dispatch(
      reqToGetFeedbackById({
        id: feedbackId,
        onSuccess: () => {
          setShowModal(true);
        },
        onFailure: () => {},
      })
    );
  };

  const handleAssignFeedback = (feedbackId) => {
    setSelectedFeedbackId(feedbackId);
    setModalMode("assign");
    dispatch(
      reqToGetFeedbackById({
        id: feedbackId,
        onSuccess: () => {
          setShowModal(true);
        },
        onFailure: () => {},
      })
    );
  };

  const handleChangeStatus = (feedbackId) => {
    setSelectedFeedbackId(feedbackId);
    setModalMode("status");
    dispatch(
      reqToGetFeedbackById({
        id: feedbackId,
        onSuccess: () => {
          setShowModal(true);
        },
        onFailure: () => {},
      })
    );
  };

  const handleChangePriority = (feedbackId) => {
    setSelectedFeedbackId(feedbackId);
    setModalMode("priority");
    dispatch(
      reqToGetFeedbackById({
        id: feedbackId,
        onSuccess: () => {
          setShowModal(true);
        },
        onFailure: () => {},
      })
    );
  };

  const handleDeleteFeedback = (feedbackId) => {
    setSelectedFeedbackId(feedbackId);
    setModalMode("delete");
    setShowModal(true);
  };

  const handleCreateFeedback = () => {
    setModalMode("create");
    setSelectedFeedbackId(null);
    dispatch(clearFeedbackDetail());
    setShowModal(true);
  };

  // Form submission - UPDATED
  const handleFormSubmit = (values, { setSubmitting, resetForm }) => {
    setCreateProgramLoader(true);

    switch (modalMode) {
      case "create":
        const createPayload = {
          type: parseInt(values.feedbackType),
          title: values.title,
          description: values.description,
          priority: parseInt(values.priority),
        };
        dispatch(
          reqToCreateFeedBack({
            data: createPayload,
            onSuccess: () => {
              setCreateProgramLoader(false);
              setSubmitting(false);
              resetForm();
              setShowModal(false);
              fetchFeedbackList();
            },
            onFailure: () => {
              setCreateProgramLoader(false);
              setSubmitting(false);
            },
          })
        );
        break;

      case "edit":
        const editPayload = {
          type: parseInt(values.feedbackType),
          title: values.title,
          description: values.description,
          priority: parseInt(values.priority),
        };
        dispatch(
          reqToUpdateFeedback({
            id: selectedFeedbackId,
            data: editPayload,
            onSuccess: () => {
              setCreateProgramLoader(false);
              setSubmitting(false);
              resetForm();
              setShowModal(false);
              fetchFeedbackList();
            },
            onFailure: () => {
              setCreateProgramLoader(false);
              setSubmitting(false);
            },
          })
        );
        break;

      case "assign":
        dispatch(
          reqToAssignFeedback({
            id: selectedFeedbackId,
            data: { assigneeId: parseInt(values.assignee) },
            onSuccess: () => {
              setCreateProgramLoader(false);
              setSubmitting(false);
              setShowModal(false);
              fetchFeedbackList();
            },
            onFailure: () => {
              setCreateProgramLoader(false);
              setSubmitting(false);
            },
          })
        );
        break;

      case "status":
        dispatch(
          reqToUpdateFeedbackStatus({
            id: selectedFeedbackId,
            data: parseInt(values.status),
            onSuccess: () => {
              setCreateProgramLoader(false);
              setSubmitting(false);
              setShowModal(false);
              fetchFeedbackList();
            },
            onFailure: () => {
              setCreateProgramLoader(false);
              setSubmitting(false);
            },
          })
        );
        break;

      case "priority":
        dispatch(
          reqToUpdateFeedbackPriority({
            id: selectedFeedbackId,
            data: { priority: parseInt(values.priority) },
            onSuccess: () => {
              setCreateProgramLoader(false);
              setSubmitting(false);
              setShowModal(false);
              fetchFeedbackList();
            },
            onFailure: () => {
              setCreateProgramLoader(false);
              setSubmitting(false);
            },
          })
        );
        break;

      default:
        setCreateProgramLoader(false);
        setSubmitting(false);
    }
  };

  // Form configurations
  const getModalTitle = () => {
    switch (modalMode) {
      case "create":
        return "Add Feedback";
      case "edit":
        return "Edit Feedback";
      case "view":
        return "View Feedback Details";
      case "assign":
        return "Assign Feedback";
      case "status":
        return "Change Status";
      case "priority":
        return "Change Priority";
      case "delete":
        return "Delete Feedback";
      default:
        return "Feedback";
    }
  };

  const getConfirmButtonText = () => {
    if (modalMode === "view") return "Close";
    if (modalMode === "delete") return "Delete";
    return createProgramLoader || updateLoader ? "Submitting..." : "Submit";
  };

  const formRef = useRef();

  // Validation schemas
  const validationSchema = Yup.object({
    feedbackType: Yup.string().required("Feedback type is required"),
    title: Yup.string()
      .required("Title is required")
      .min(5, "Title must be at least 5 characters")
      .max(100, "Title must be less than 100 characters"),
    description: Yup.string()
      .required("Description is required")
      .min(10, "Description must be at least 10 characters")
      .max(1000, "Description must be less than 1000 characters"),
    priority: Yup.string().required("Priority is required"),
  });

  const assignValidationSchema = Yup.object({
    assignee: Yup.string().required("Assignee is required"),
  });

  const statusValidationSchema = Yup.object({
    status: Yup.string().required("Status is required"),
  });

  const priorityValidationSchema = Yup.object({
    priority: Yup.string().required("Priority is required"),
  });

  const getInitialValues = () => {
    if (
      modalMode === "view" ||
      modalMode === "edit" ||
      modalMode === "create"
    ) {
      return {
        feedbackType: feedbackDetail?.type?.toString() || "",
        title: feedbackDetail?.title || "",
        description: feedbackDetail?.description || "",
        priority: feedbackDetail?.priority?.toString() || "",
      };
    }
    if (modalMode === "assign") {
      return {
        assignee: feedbackDetail?.assignedStaff?.id?.toString() || "",
      };
    }
    if (modalMode === "status") {
      return {
        status: feedbackDetail?.status?.toString() || "",
      };
    }
    if (modalMode === "priority") {
      return {
        priority: feedbackDetail?.priority?.toString() || "",
      };
    }
    return {};
  };

  const getValidationSchema = () => {
    switch (modalMode) {
      case "create":
      case "edit":
        return validationSchema;
      case "assign":
        return assignValidationSchema;
      case "status":
        return statusValidationSchema;
      case "priority":
        return priorityValidationSchema;
      default:
        return null;
    }
  };

  const handleOpenModal = (item) => {
    setSelectedFeedback(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFeedback(null);
  };

  return (
    <>
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-3 sm:flex-nowrap flex-wrap">
          <div className="comman-form flex items-center gap-2 flex-wrap sm:w-fit w-full">
            <div className="form-group min-w-full sm:min-w-[186px] z-[99]">
              <Select
                options={typeOptions}
                value={typeFilter}
                onChange={(option) => setTypeFilter(option || typeOptions[0])}
                placeholder="All Types"
                isClearable={false}
                isSearchable={false}
                menuPlacement="auto"
              />
            </div>
            <div className="form-group min-w-full sm:min-w-[186px] z-[99]">
              <Select
                options={statusOptions}
                value={statusFilter}
                onChange={(option) =>
                  setStatusFilter(option || statusOptions[0])
                }
                placeholder="All Statuses"
                isClearable={false}
                isSearchable={false}
                menuPlacement="auto"
              />
            </div>
            <div className="form-group min-w-full sm:min-w-[186px] z-[99]">
              <Select
                options={sortOptions}
                value={sortOption}
                onChange={(option) => setSortOption(option || sortOptions[0])}
                placeholder="Newest"
                isClearable={false}
                isSearchable={false}
                menuPlacement="auto"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap justify-end">
            <button
              type="button"
              className="normal-btn"
              onClick={fetchInitialData}
            >
              {BlackRefrechIcon} Refresh
            </button>
            {/* <button type="button" className="normal-btn">
            {BlackExportIcon} Export
          </button> */}
            {/* <button
            type="button"
            className="normal-btn"
            onClick={handleCreateFeedback}
          >
            Add Feedback
          </button> */}
          </div>
        </div>

        <div className="w-full overflow-auto mt-4 rounded-lg max-h-[1000px] common-scroll">
          <table className="min-w-[500px] w-full table-auto border-collapse">
            <thead>
              <tr>
                <th>Feedback</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Votes</th>
                <th>Submitted</th>
                <th>Assignee</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="py-10 text-center text-body text-[#5C5B62]"
                  >
                    No feedback found. Try adjusting your filters.
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => {
                  const absoluteIndex = index;
                  const typeName = getTypeNameById(item.type);
                  const priorityName = getPriorityNameById(item.priority);
                  const statusName = getStatusNameById(item.status);
                  const assigneeName =
                    item.assignedStaff?.name || item.user?.name || "Unassigned";
                  const submittedDate = new Date(
                    item.createdAt
                  ).toLocaleDateString();
                  const companyName =
                    item.user?.company || item.company || "N/A";

                  return (
                    <tr key={item.id} className="border-b border-[#FAFAFA]">
                      <td>
                        <div>
                          <p className="text-sub_span !font-bold text-black mb-2">
                            {item.title}
                          </p>
                          <div className="flex items-center gap-2">
                            <span className="bg-[#FFE4D7] px-2 py-1 min-w-[57px] inline-block text-center rounded-full text-body text-[#F45C0C]">
                              FDBK-{item.id.toString().padStart(3, "0")}
                            </span>
                            <span className="bg-[#F6FFED] px-2 py-1 min-w-[57px] inline-block text-center rounded-full text-body text-[#277600]">
                              {typeName}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td>
                        <span
                          className={`px-2 py-1 min-w-[57px] inline-block text-center rounded-full text-body ${getStatusStyle(
                            statusName
                          )}`}
                        >
                          {statusName}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`px-2 py-1 min-w-[57px] inline-block text-center rounded-full text-body ${getPriorityStyle(
                            priorityName
                          )}`}
                        >
                          {priorityName}
                        </span>
                      </td>

                      <td>
                        <div className="flex items-center gap-2 text-gray-600">
                          <span className="w-[36px] h-[30px] flex items-center justify-center text-span text-black">
                            {parseInt(item.total_votes) || 0}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="space-y-2">
                          <div className="text-sub_span !font-medium text-black">
                            {submittedDate}
                          </div>
                          <div className="text-body text-dark">
                            {companyName}
                          </div>
                        </div>
                      </td>

                      <td>
                        <span className="text-body text-[#B4B4B4] !font-medium">
                          {assigneeName}
                        </span>
                      </td>

                      <td className="relative">
                        <button
                          type="button"
                          onClick={() =>
                            setOpenRow(
                              openRow === absoluteIndex ? null : absoluteIndex
                            )
                          }
                          className="inline-flex items-center justify-center gap-x-2 rounded-md px-3 py-1"
                        >
                          {threeDotsIcon}
                        </button>

                        {openRow === absoluteIndex && (
                          <div
                            ref={dropdownRef}
                            className="absolute dropdown right-2 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-[100]"
                          >
                            <div className="py-1">
                              <div
                                className="block px-4 py-2 dropdown-item cursor-pointer text-sub_span !font-medium hover:bg-[#4285F4] hover:text-[#DAFA7B] flex gap-2 items-center"
                                onClick={() => handleOpenModal(item)}
                              >
                                {ViewDetailsIcon}
                                View details
                              </div>
                              <div
                                className="block px-4 py-2 dropdown-item cursor-pointer text-sub_span !font-medium hover:bg-[#4285F4] hover:text-[#DAFA7B] flex gap-2 items-center"
                                onClick={() => handleAssignFeedback(item.id)}
                              >
                                {AssignIcon} Assign
                              </div>
                              <div
                                className="block px-4 py-2 dropdown-item cursor-pointer text-sub_span !font-medium hover:bg-[#4285F4] hover:text-[#DAFA7B] flex gap-2 items-center"
                                onClick={() => handleChangePriority(item.id)}
                              >
                                {ChangePriorityIcon}
                                Change priority
                              </div>
                              <div
                                className="block px-4 py-2 dropdown-item cursor-pointer text-sub_span !font-medium hover:bg-[#4285F4] hover:text-[#DAFA7B] flex gap-2 items-center"
                                onClick={() => handleChangeStatus(item.id)}
                              >
                                {UpdatedStatusIcon}
                                Update status
                              </div>
                              {/* <div
                              className="block px-4 py-2 dropdown-item cursor-pointer text-sub_span !font-medium hover:bg-[#4285F4] hover:text-[#DAFA7B] flex gap-2 items-center"
                              onClick={() => handleEditFeedback(item.id)}
                            >
                              {ViewDetailsIcon}
                              Edit
                            </div> */}
                              <div
                                className="block px-4 py-2 dropdown-item cursor-pointer text-sub_span !font-medium hover:bg-[#4285F4] hover:text-[#DAFA7B] flex gap-2 items-center"
                                onClick={() => handleDeleteFeedback(item.id)}
                              >
                                {DeleteRedIcon}
                                Delete
                              </div>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-2 flex-wrap items-center mt-6">
          <p className="text-sub_span mb-0 !font-medium text-black">
            {totalCount === 0
              ? "No Feedback"
              : `Showing ${paginatedData.length} of ${totalCount} Feedback`}
          </p>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1 || totalCount === 0}
              className="border-none outline-none disabled:opacity-50"
            >
              {PaginationLeftArrow}
            </button>

            {/* Only show pagination if we have data */}
            {totalCount > 0 &&
              Array.from({ length: totalPages }, (_, index) => index + 1).map(
                (pageNum) => (
                  <button
                    key={pageNum}
                    type="button"
                    onClick={() => goToPage(pageNum)}
                    className={`flex items-center justify-center rounded-[8px] text-body-span w-[28px] h-[28px] text-center border-[1px] ${
                      currentPage === pageNum
                        ? "bg-[#4285F4] text-white border-[#4285F4]"
                        : "text-gray-700 border-[#5C5B62] hover:bg-gray-100"
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              )}

            <button
              type="button"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalCount === 0}
              className="border-none outline-none disabled:opacity-50"
            >
              {PaginationRightArrow}
            </button>
          </div>
          <div className="flex items-center relative">
            <select
              id="pageSize"
              value={pageSize}
              onChange={handlePageSizeChange}
              className="w-full text-sub_span text-black border-2 border-[#FAFAFA] rounded-[16px] p-2 pl-3 pr-6 outline-none appearance-none"
            >
              {[10, 20, 30, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
              {DownArrowSelect}
            </div>
          </div>
        </div>
      </div>

      {/* Modal for all operations - UPDATED */}
      <PopUpModal
        visible={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleModalConfirm} // Always pass the handler
        title={getModalTitle()}
        confirmText={getConfirmButtonText()}
        cancelText={modalMode === "delete" ? "Cancel" : "Close"}
        confirmDisabled={createProgramLoader || updateLoader}
        loading={createProgramLoader || updateLoader}
        size={modalMode === "view" ? "large" : "medium"}
        hideConfirm={modalMode === "view"} // Show confirm button for all modals
      >
        {modalMode === "delete" ? (
          <div className="p-4">
            <p>
              Are you sure you want to delete this feedback? This action cannot
              be undone.
            </p>
          </div>
        ) : (
          <Formik
            initialValues={getInitialValues()}
            validationSchema={getValidationSchema()}
            onSubmit={handleFormSubmit}
            enableReinitialize
          >
            {({
              values,
              setFieldValue,
              errors,
              touched,
              handleSubmit,
              isSubmitting,
            }) => (
              <Form ref={formRef} onSubmit={handleSubmit}>
                <div className="space-y-4 p-1">
                  {/* View/Edit/Create Form */}
                  {(modalMode === "view" ||
                    modalMode === "edit" ||
                    modalMode === "create") && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Type {modalMode !== "view" && "*"}
                        </label>
                        {modalMode === "view" ? (
                          <div className="p-2 border border-gray-300 rounded-md bg-gray-50">
                            {getTypeNameById(feedbackDetail?.type) || "N/A"}
                          </div>
                        ) : (
                          <Select
                            options={feedbackTypeOptions}
                            value={feedbackTypeOptions.find(
                              (option) => option.value === values.feedbackType
                            )}
                            onChange={(selectedOption) =>
                              setFieldValue(
                                "feedbackType",
                                selectedOption?.value || ""
                              )
                            }
                            isSearchable={false}
                            placeholder="Select a feedback type"
                            isDisabled={modalMode === "view"}
                          />
                        )}
                        <ErrorMessage
                          name="feedbackType"
                          component="div"
                          className="text-red-500 text-xs mt-1"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Title {modalMode !== "view" && "*"}
                        </label>
                        {modalMode === "view" ? (
                          <div className="p-2 border border-gray-300 rounded-md bg-gray-50">
                            {feedbackDetail?.title || "N/A"}
                          </div>
                        ) : (
                          <Field
                            type="text"
                            name="title"
                            className="input-field w-full border border-gray-300 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Brief description of your feedback"
                            disabled={modalMode === "view"}
                          />
                        )}
                        <ErrorMessage
                          name="title"
                          component="div"
                          className="text-red-500 text-xs mt-1"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Description {modalMode !== "view" && "*"}
                        </label>
                        {modalMode === "view" ? (
                          <div className="p-2 border border-gray-300 rounded-md bg-gray-50 min-h-[100px]">
                            {feedbackDetail?.description || "N/A"}
                          </div>
                        ) : (
                          <Field
                            as="textarea"
                            name="description"
                            rows={4}
                            className="input-field w-full border border-gray-300 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Please provide detailed information about your feedback"
                            disabled={modalMode === "view"}
                          />
                        )}
                        <ErrorMessage
                          name="description"
                          component="div"
                          className="text-red-500 text-xs mt-1"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Priority {modalMode !== "view" && "*"}
                        </label>
                        {modalMode === "view" ? (
                          <div className="p-2 border border-gray-300 rounded-md bg-gray-50">
                            {getPriorityNameById(feedbackDetail?.priority) ||
                              "N/A"}
                          </div>
                        ) : (
                          <Select
                            options={priorityOptions}
                            value={priorityOptions.find(
                              (option) => option.value === values.priority
                            )}
                            onChange={(selectedOption) =>
                              setFieldValue(
                                "priority",
                                selectedOption?.value || ""
                              )
                            }
                            isSearchable={false}
                            placeholder="Select priority"
                            isDisabled={modalMode === "view"}
                          />
                        )}
                        <ErrorMessage
                          name="priority"
                          component="div"
                          className="text-red-500 text-xs mt-1"
                        />
                      </div>
                    </>
                  )}

                  {/* Assign Form */}
                  {/* Assign Form */}
                  {modalMode === "assign" && (
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Assignee *
                      </label>
                      <Select
                        options={staffOptions}
                        value={staffOptions.find(
                          (option) => option.value === values.assignee
                        )}
                        onChange={(selectedOption) =>
                          setFieldValue("assignee", selectedOption?.value || "")
                        }
                        isSearchable={true}
                        placeholder="Select an assignee"
                        styles={{
                          menu: (provided) => ({
                            ...provided,
                            maxHeight: 150, // adjust as you like
                            overflowY: "auto",
                            zIndex: 9999, // ensures it shows above modals
                          }),
                          menuList: (provided) => ({
                            ...provided,
                            maxHeight: 150,
                            overflowY: "auto",
                          }),
                        }}
                      />
                      <ErrorMessage
                        name="assignee"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />

                      {/* Show current assignee if any */}
                      {feedbackDetail?.assignedStaff && (
                        <div className="mt-2 p-2 bg-blue-50 rounded-md">
                          <p className="text-sm text-blue-700">
                            Currently assigned to:{" "}
                            <strong>{feedbackDetail.assignedStaff.name}</strong>
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Status Form */}
                  {modalMode === "status" && (
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Status *
                      </label>
                      <Select
                        options={statusOptionsFormatted}
                        value={statusOptionsFormatted.find(
                          (option) => option.value === values.status
                        )}
                        onChange={(selectedOption) =>
                          setFieldValue("status", selectedOption?.value || "")
                        }
                        isSearchable={false}
                        placeholder="Select status"
                        styles={{
                          menu: (provided) => ({
                            ...provided,
                            maxHeight: 150, // adjust as you like
                            overflowY: "auto",
                            zIndex: 9999, // ensures it shows above modals
                          }),
                          menuList: (provided) => ({
                            ...provided,
                            maxHeight: 150,
                            overflowY: "auto",
                          }),
                        }}
                      />

                      <ErrorMessage
                        name="status"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                  )}

                  {/* Priority Form */}
                  {modalMode === "priority" && (
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Priority *
                      </label>
                      <Select
                        options={priorityOptions}
                        value={priorityOptions.find(
                          (option) => option.value === values.priority
                        )}
                        onChange={(selectedOption) =>
                          setFieldValue("priority", selectedOption?.value || "")
                        }
                        isSearchable={false}
                        placeholder="Select priority"
                        styles={{
                          menu: (provided) => ({
                            ...provided,
                            maxHeight: 150, // adjust as you like
                            overflowY: "auto",
                            zIndex: 9999, // ensures it shows above modals
                          }),
                          menuList: (provided) => ({
                            ...provided,
                            maxHeight: 150,
                            overflowY: "auto",
                          }),
                        }}
                      />
                      <ErrorMessage
                        name="priority"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                  )}

                  {/* Additional view-only fields */}
                  {modalMode === "view" && feedbackDetail && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Status
                        </label>
                        <div className="p-2 border border-gray-300 rounded-md bg-gray-50">
                          {getStatusNameById(feedbackDetail.status) || "N/A"}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Submitted Date
                        </label>
                        <div className="p-2 border border-gray-300 rounded-md bg-gray-50">
                          {new Date(feedbackDetail.createdAt).toLocaleString()}
                        </div>
                      </div>
                      {feedbackDetail.updatedAt && (
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Last Updated
                          </label>
                          <div className="p-2 border border-gray-300 rounded-md bg-gray-50">
                            {new Date(
                              feedbackDetail.updatedAt
                            ).toLocaleString()}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <button type="submit" className="hidden" />
              </Form>
            )}
          </Formik>
        )}
      </PopUpModal>
      <FeedbackDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        feedbackItem={selectedFeedback}
      />
    </>
  );
};

export default FeedbackList;
